/**
 * Performance monitoring utilities to track application performance
 */

// Supported performance metrics
export type PerformanceMetric = 
  | 'page-load'       // Full page load time
  | 'time-to-interactive' // Time until page is fully interactive
  | 'first-contentful-paint' // First content appears
  | 'api-request'     // API request timing
  | 'image-load'      // Image loading performance
  | 'component-render' // Component render timing
  | 'resource-load'   // Resource loading timing
  | 'memory-usage'    // Memory usage snapshot
  | 'long-task'       // Tasks taking >50ms
  | 'custom'          // Custom performance measurement

// Performance data structure
export interface PerformanceData {
  metric: PerformanceMetric;
  value: number;
  label?: string;
  timestamp: number;
  context?: Record<string, any>;
}

// Options for performance tracking
export interface PerformanceTrackingOptions {
  sampleRate?: number; // 0.0-1.0, percentage of measurements to record
  maxBufferSize?: number; // Max events to buffer before sending
  sendInterval?: number; // Milliseconds between sending batches
  debug?: boolean; // Enable console logging
}

// Default configuration
const DEFAULT_OPTIONS: PerformanceTrackingOptions = {
  sampleRate: 0.1, // Sample 10% of measurements
  maxBufferSize: 50,
  sendInterval: 10000, // 10 seconds
  debug: false
};

// Store performance measurements
let performanceBuffer: PerformanceData[] = [];
let options: PerformanceTrackingOptions = DEFAULT_OPTIONS;
let isInitialized = false;
let sendIntervalId: number | null = null;

/**
 * Initialize performance monitoring
 */
export function initPerformanceMonitoring(customOptions?: PerformanceTrackingOptions) {
  if (isInitialized) return;
  
  // Merge custom options with defaults
  options = { ...DEFAULT_OPTIONS, ...customOptions };
  
  // Setup automatic sending on interval
  if (options.sendInterval) {
    sendIntervalId = window.setInterval(() => {
      sendPerformanceData();
    }, options.sendInterval);
  }
  
  // Setup performance observers if browser supports them
  if (typeof PerformanceObserver !== 'undefined') {
    try {
      // Observe long tasks
      const longTaskObserver = new PerformanceObserver((list) => {
        list.getEntries().forEach((entry) => {
          recordPerformanceMetric('long-task', entry.duration, entry.name, {
            startTime: entry.startTime,
            detail: entry.toJSON()
          });
        });
      });
      longTaskObserver.observe({ entryTypes: ['longtask'] });
      
      // Observe paint timing
      const paintObserver = new PerformanceObserver((list) => {
        list.getEntries().forEach((entry) => {
          if (entry.name === 'first-contentful-paint') {
            recordPerformanceMetric('first-contentful-paint', entry.startTime);
          }
        });
      });
      paintObserver.observe({ entryTypes: ['paint'] });
      
      // Observe resource timing
      const resourceObserver = new PerformanceObserver((list) => {
        list.getEntries().forEach((entry) => {
          // Cast to PerformanceResourceTiming which has initiatorType
          const resourceEntry = entry as PerformanceResourceTiming;
          if (resourceEntry.initiatorType === 'fetch' || resourceEntry.initiatorType === 'xmlhttprequest') {
            recordPerformanceMetric('api-request', entry.duration, entry.name);
          } else if (resourceEntry.initiatorType === 'img') {
            recordPerformanceMetric('image-load', entry.duration, entry.name);
          } else {
            recordPerformanceMetric('resource-load', entry.duration, entry.name);
          }
        });
      });
      resourceObserver.observe({ entryTypes: ['resource'] });
    } catch (e) {
      console.error('Error setting up performance observers:', e);
    }
  }
  
  // Record page load time when window loads
  window.addEventListener('load', () => {
    const pageLoadTime = performance.now();
    recordPerformanceMetric('page-load', pageLoadTime);
    
    // Time to interactive - when the page has loaded and becomes responsive
    // (simplified approximation)
    window.setTimeout(() => {
      const timeToInteractive = performance.now();
      recordPerformanceMetric('time-to-interactive', timeToInteractive);
    }, 0);
  });
  
  // Periodically capture memory usage if available
  // @ts-ignore: Chrome-specific memory API
  if (performance.memory) {
    window.setInterval(() => {
      // @ts-ignore: Chrome-specific memory API
      recordPerformanceMetric('memory-usage', performance.memory.usedJSHeapSize);
    }, 30000); // Every 30 seconds
  }
  
  isInitialized = true;
  
  if (options.debug) {
    console.log('Performance monitoring initialized', options);
  }
}

/**
 * Record a performance metric
 */
export function recordPerformanceMetric(
  metric: PerformanceMetric,
  value: number,
  label?: string,
  context?: Record<string, any>
) {
  // Sample based on sampleRate
  if (Math.random() > (options.sampleRate || 1)) {
    return;
  }
  
  const performanceData: PerformanceData = {
    metric,
    value,
    label,
    timestamp: Date.now(),
    context
  };
  
  performanceBuffer.push(performanceData);
  
  if (options.debug) {
    console.log(`Performance metric: ${metric}`, performanceData);
  }
  
  // Send if buffer is full
  if (performanceBuffer.length >= (options.maxBufferSize || 50)) {
    sendPerformanceData();
  }
}

/**
 * Measure execution time of a function
 */
export function measureExecutionTime<T extends (...args: any[]) => any>(
  fn: T,
  metricName: string
): (...args: Parameters<T>) => ReturnType<T> {
  return (...args: Parameters<T>): ReturnType<T> => {
    const start = performance.now();
    const result = fn(...args);
    const duration = performance.now() - start;
    
    recordPerformanceMetric('custom', duration, metricName);
    
    return result;
  };
}

/**
 * Measure component render time
 */
export function startComponentRenderTimer(componentName: string): () => void {
  const startTime = performance.now();
  
  return () => {
    const duration = performance.now() - startTime;
    recordPerformanceMetric('component-render', duration, componentName);
  };
}

/**
 * Send performance data to backend
 */
function sendPerformanceData() {
  if (performanceBuffer.length === 0) return;
  
  // Clone the buffer and clear it
  const dataToSend = [...performanceBuffer];
  performanceBuffer = [];
  
  if (options.debug) {
    console.log(`Sending ${dataToSend.length} performance metrics to backend`);
  }
  
  // Send data to backend analytics endpoint
  // This would be implemented as a fetch or axios call
  // fetch('/api/analytics/performance', {
  //   method: 'POST',
  //   headers: { 'Content-Type': 'application/json' },
  //   body: JSON.stringify(dataToSend)
  // }).catch(error => {
  //   console.error('Failed to send performance data:', error);
  // });
}

/**
 * Clean up performance monitoring
 */
export function cleanupPerformanceMonitoring() {
  if (sendIntervalId !== null) {
    window.clearInterval(sendIntervalId);
    sendIntervalId = null;
  }
  
  // Send any remaining data
  sendPerformanceData();
  
  isInitialized = false;
} 